import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { Controller, useFieldArray, useForm } from "react-hook-form";
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import "react-datepicker/dist/react-datepicker.css";
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { BankSearchFormI, ExpenseGlClassificationFormI } from '../../../resources/form-props';
import { BankExpenseValidate, GLSearchFormValidate } from '../../../resources/form-validator';
import { useChartAccountApi } from '../../../resources/hooks/api/chartAccountApiHook';
import { useGlClassificationApi } from '../../../resources/hooks/api/glClassificationApi';
import { getBranch } from '../../../store/user/selectors';
import { useAllVendors } from '../../../resources/useAllVendors';
import { accountLabel } from '../../Tax1099Vendors/VendorFormModal';
import { activeVendorOptions, matchVendorByPayee } from './vendorMatch';

import { useTranslation } from 'react-i18next';
import { SelectInstance } from 'react-select'; // Import the correct type
import ChartAccountModal from '../../../components/Modal/ChartOfAccount/ChartAccountModal';
import SAlert from '../../../components/SAlert';
import SplitModal from '../../../components/Modal/ExpenseClassification/SplitModal'; // Import your new SplitModal component
import SelectCustomOption from '../../../components/SelectCustomOption';
import { GL_VP, SELECT_CUSTOM_STYLE, URLS } from '../../../resources/constants';
import { calculateMaxHeightContent, formatTransactionDate, usdCurrencyFormat } from '../../../resources/functions';
import '../GlClassification.css';


interface Props {

}

const ExpenseGlClassification: React.FC<Props> = () => {
    const { t } = useTranslation("scenes.glclassification.expense");
    const { glTransactionlist, bankGlUpdate } = useGlClassificationApi();
    const { glAccountsList } = useChartAccountApi();
    const urlParams = useParams();
    const navigate = useNavigate();
    const branchData = useSelector(getBranch)

    // Vendors for this branch, from the shared cache the 1099 screens use. A
    // vendor created in Payee Review invalidates that cache, so a fresh one is
    // visible here without a reload.
    const { vendors } = useAllVendors({ branchId: branchData?.id ? Number(branchData.id) : null });
    const vendorNameById = useMemo(() => {
        const byId = new Map<number, string>();
        (vendors || []).forEach((vendor: any) => {
            const name = `${vendor?.display_name || ''}`.trim();
            if (vendor?.id && name) {
                byId.set(Number(vendor.id), name);
            }
        });
        return byId;
    }, [vendors]);

    // A contractor's default GL account, as the option the debit dropdown holds.
    const vendorDefaultAccountById = useMemo(() => {
        const byId = new Map<number, { value: number; label: string }>();
        (vendors || []).forEach((vendor: any) => {
            if (vendor?.id && vendor.default_account_chart) {
                byId.set(Number(vendor.id), {
                    value: vendor.default_account_chart.id,
                    label: accountLabel(vendor.default_account_chart),
                });
            }
        });
        return byId;
    }, [vendors]);
    // Rows already offered their vendor's default, so an account the user
    // cleared on purpose is not put back when the vendor list refreshes.
    const prefilledRecordIds = useRef<Set<number>>(new Set());
    // Rows whose check payee has already been matched to a vendor, so a vendor
    // the user clears or changes is not put back when the list refreshes.
    const preselectedRecordIds = useRef<Set<number>>(new Set());
    // A post that moves saved vendors waits here for the user's confirmation.
    const [pendingPost, setPendingPost] = useState<{ records: any[]; toRemoveItems: number[]; moves: any[] } | null>(null);
    const vendorOptions = useMemo(() => activeVendorOptions(vendors), [vendors]);

    const [records, setRecords] = useState<any>([]);
    //const [glAccount, setGlAccount] = useState<any[]>([]);
    const [glDebitAccount, setGlDebitAccount] = useState<any[]>([]);
    const [glCreditAccount, setGlCreditAccount] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCOAModal, setShowCOAModal] = useState<boolean>(false)
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0)
    const [selectedCalendar, setSelectedCalendar] = useState<any>({
        year: 2024,
        month: 1
    });
    const [showSplitModal, setShowSplitModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);


    const [selectCustomStyle, setSlectCustomStyle] = useState<any>(SELECT_CUSTOM_STYLE('#d1ffbd'));
    const checkAllRef = useRef<HTMLInputElement>(null);
    const selectAllRef = useRef<HTMLInputElement[]>([]);
    const [sortConfig, setSortConfig] = useState<{ key: 'transaction_at' | 'description' | null; direction: 'asc' | 'desc' }>({
    key: null,
    direction: 'asc'
    });
    const { register, trigger, control, handleSubmit, reset, setValue, getValues, formState: { errors } } = useForm<ExpenseGlClassificationFormI>({
        defaultValues: {
            banks: []
        },
        resolver: yupResolver(BankExpenseValidate)
    });
    const { fields, append: formAppend, remove: formRemove } = useFieldArray({
        control,
        name: "banks"

    });

    const { control: control2, handleSubmit: handleSubmit2, formState: { errors: errors2 } } = useForm<BankSearchFormI>({
        defaultValues: {
            month: {
                value: '01',
                label:  t('month.01', { ns: 'resources.form-validator' })
            },
            year: { value: '2024', label: '2024' },
        },
        resolver: yupResolver(GLSearchFormValidate)
    });

    useEffect(() => {
        setContentMaxHeight(calculateMaxHeightContent(131));
    })
    useEffect(() => {
        fetchTransactionList();
        fechDebitAccountList().then(r => {
            setGlDebitAccount(r);
        })
        fechCreditAccountList().then(r => {
            setGlCreditAccount(r);
        })
    }, [])
    // useEffect(() => {
    //     fetchTransactionList();
    // }, [selectedCalendar])
    useEffect(() => {
        records.forEach((record: any, index: number) => {
            const creditData = record['breakdowns'].filter((e: any) => e['account_type'] == "Credit_Deposit");
            const debitData = record['breakdowns'].filter((e: any) => e['account_type'] == "Debit_Deposit");
            /*const equityData = record['breakdowns'].filter((e: any) => e['account_type'] == "Equity");
            const bankTransferData = record['breakdowns'].filter((e: any) => e['account_type'] == "Bank Transfer");
            const otherData = record['breakdowns'].filter((e: any) => e['account_type'] == "Others");
            const adjustedBankDepositData = record['breakdowns'].filter((e: any) => e['account_type'] == "Adjusted Bank Deposit");
            */

            formAppend({
                formIndex: index,
                checked: false,
                record_id: record['id'],
                parent_id: record['parent_id'],
                description: record['description'],
                account_name: record['account_name'],
                transaction_at: record['transaction_at'],
                // vendor_id is the dropdown's working value; saved_vendor_id is
                // what the expense already carries (Payee Review, an earlier
                // post). The dropdown starts on it.
                vendor_id: record['vendor_id'] ?? null,
                saved_vendor_id: record['vendor_id'] ?? null,
                extracted_payee_name: record['extracted_payee_name'] ?? null,
                deposit: (creditData.length && creditData[0]['account_value']) || 0,
                deposit_debit_account_id: (debitData.length && debitData[0]['account_chart'] && {
                    label: debitData[0]['account_chart']['name'] + '(' + debitData[0]['account_chart']['account'] + ')',
                    value: debitData[0]['account_chart']['id']
                }) || null,
                deposit_credit_account_id: (creditData.length && creditData[0]['account_chart'] && {
                    label: creditData[0]['account_chart']['name'] + '(' + creditData[0]['account_chart']['account'] + ')',
                    value: creditData[0]['account_chart']['id']
                }) || null,
                split: [],
            })
        })
    }, [records])
    // Puts the vendor's default GL account on the row's debit dropdown. Skipped
    // for split rows (their accounts live on the split) and rows with no amount.
    // `force` is for a vendor the user just picked; the automatic paths offer
    // the default once per row.
    const applyVendorDefaultAccount = (index: number, vendorId: number | null, force: boolean) => {
        const row: any = getValues(`banks.${index}` as any);
        const account = vendorDefaultAccountById.get(Number(vendorId));
        if (!row || !account || !row.deposit) return;
        if (!force && prefilledRecordIds.current.has(row.record_id)) return;
        if (splitData.some((split: any) => split.id === row.record_id)) return;
        prefilledRecordIds.current.add(row.record_id);
        setValue(`banks.${index}.deposit_debit_account_id`, account, { shouldDirty: true });
    };
    // A contractor's default GL account replaces whatever the row loaded with.
    // This screen lists only unposted rows (updated_at IS NULL; posting stamps
    // it), so that account is always the AI classifier's guess or the
    // uncategorized fallback -- never one a user chose. Once per row, so a
    // change made here survives a vendor list refresh. Split rows carry their
    // accounts on the split, and rows without an amount keep the dropdown
    // disabled. Nothing is saved until the user posts.
    useEffect(() => {
        fields.forEach((field: any, index: number) => {
            applyVendorDefaultAccount(index, field.saved_vendor_id, false);
        });
    }, [fields, vendorDefaultAccountById])
    // A check whose payee names exactly one active vendor starts with that
    // vendor selected. Nothing is saved until the user posts.
    useEffect(() => {
        if (!vendorOptions.length) return;
        fields.forEach((field: any, index: number) => {
            if (field.saved_vendor_id || preselectedRecordIds.current.has(field.record_id)) return;
            const vendorId = matchVendorByPayee(field.extracted_payee_name, vendorOptions);
            if (!vendorId) return;
            preselectedRecordIds.current.add(field.record_id);
            setValue(`banks.${index}.vendor_id`, vendorId, { shouldDirty: true });
            applyVendorDefaultAccount(index, vendorId, false);
        });
    }, [fields, vendorOptions])
    useEffect(() => {
        if (fields.length == 0) {
            if (checkAllRef.current) {
                checkAllRef.current.checked = false;
            }
        }
    }, [fields])


    const fetchTransactionList = () => {
        const params: any = {
            "page": 1,
            "per_page": 500,
            year: urlParams['year'],
            month: urlParams['month'],
            branch_id: +branchData['id']
        }

        params['id'] = 0;
        params['account_type'] = 1;

        setIsLoading(true);
        glTransactionlist(params, (message: string, resp: any) => {
            reset();
            setRecords(resp.data.data.result);
            setIsLoading(false);
        }, (message: string, resp: any) => {
            setIsLoading(false);
            toast.error(message);
        })
    }
    // console.log(records);
    // const fechAccountList = (searchText: string = ''): Promise<any[]> => {
    //     return new Promise<any>((resolve, reject) => {
    //         const params: any = {
    //             "page": 1,
    //             "per_page": 10
    //         }
    //         if ( searchText ) {
    //             params['name'] = searchText;
    //         }
    //         glAccountsList(params, (message: string, resp: any) => {
    //             console.log(resp);
    //             const data = resp.data.data.result.map((e: any) => ({
    //                 label: e['name'] + `(${e['account']})`,
    //                 value: e['id']
    //             }));
    //             return resolve(data);
    //             //console.log(resp);
    //             // setGlAccount(data);
    //         }, (message: string, resp: any) => {
    //             toast.error(message);
    //             return resolve([]);
    //         })
    //     })
    // }

    const fechDebitAccountList = (searchText: string = ''): Promise<any[]> => {

        return new Promise<any>((resolve, reject) => {
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }
            params['parent_account_not_in'] = GL_VP.BANK_ACCOUNT.account.join(',');
            glAccountsList(params, (message: string, resp: any) => {
                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id']
                }));
                return resolve(data);
                //console.log(data);
                // setGlAccount(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve([]);
            })
        })
    }

    const fechCreditAccountList = (searchText: string = ''): Promise<any[]> => {
        return new Promise<any>((resolve1, reject1) => {
            const params: any = {
                "page": 1,
                "per_page": 10,
                branch_id: +branchData['id']
            }
            if (searchText) {
                params['name'] = searchText;
            }
            params['parent_account_in'] = GL_VP.BANK_ACCOUNT.account.join(',');
            glAccountsList(params, (message: string, resp: any) => {

                const data = resp.data.data.result.map((e: any) => ({
                    label: e['name'] + `(${e['account']})`,
                    value: e['id']
                }));
                return resolve1(data);
                //console.log(resp);
                // setGlAccount(data);
            }, (message: string, resp: any) => {
                toast.error(message);
                return resolve1([]);
            })
        })
    }

    // const filterAccountList = async (inputValue: string) => {
    //     const data = await fechAccountList(inputValue)
    //     return data;
    // };

    const filterDebitAccountList = async (inputValue: string) => {
        const data = await fechDebitAccountList(inputValue)
        return data;
    };
    const filterCreditAccountList = async (inputValue: string) => {
        const data = await fechCreditAccountList(inputValue)
        return data;
    };


    const handleCheckAllChange = (e: any) => {
        if (checkAllRef.current) {
            checkAllRef.current.checked = e.target.checked;

            fields.forEach((record: any, index: number) => {
                setValue(`banks.${index}.checked`, e.target.checked)
            })

            /*records.map((record: any, index: number) => {
                setValue(`banks.${index}.checked`, e.target.checked)
            })*/
        }
        // setChecked(
        //     e.target.checked ? records.map((c: any) => c.id) : []
        // );
    };

    const handleCheckAll = (e: any, index: number) => {
        setValue(`banks.${index}.checked`, e.target.checked);
        if (e.target.checked) {
            const checkedRecords = fields.filter((record: any, index: number) => getValues(`banks.${index}.checked`) === true);

            if (checkedRecords.length == fields.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = true;
                }
            }
        } else {
            const uncheckedRecords = fields.filter((record: any, index: number) => getValues(`banks.${index}.checked`) === false);

            if (uncheckedRecords.length) {
                if (checkAllRef.current) {
                    checkAllRef.current.checked = false;
                }
            }
        }
    }

    // const handleSplitClick = (record: any, rowId : number) => {
    //     console.log(rowId)
    //     setSelectedRecord(record);
    //     setShowSplitModal(true);
    // };
    const [selectedRowID, setSelectedRowID] = useState<number | null>(null);
    const [indexNo, setIndexNo] = useState<number | null>(null);
    const handleSplitClick = (data: any, id: number) => {


        // Optionally find the specific item in the array and add the index if needed
        // For simplicity, we'll just push the index to the end of the array

        setSelectedRecord(data);
        setShowSplitModal(true);
    };
    const [splitData, setSplitData] = React.useState<any[]>([]);
    const handleSplitSave = (rowID: number, indexNo: number, splits: any[]) => {
        // Create new splits with the rowID added
        const newSplits = splits.map(split => ({ ...split }));

        // Update splitData state
        setSplitData(prevData => {
            // Filter out existing splits with the same rowID
            const updatedSplits = prevData.filter(split => split.rowID !== rowID);
            // Merge new splits with the updated data
            return [...updatedSplits, ...newSplits];
        });

        // Remove rowID from newSplits
        const finalSplits = newSplits.map(split => {
            const { rowID, ...rest } = split;
            return rest;
        });

        // Ensure setValue is called with the latest splitData
        setValue(`banks.${indexNo}.split`, finalSplits);


    };






    const processData = (data: any) => {
        //console.log(data);
        const toRemoveItems: number[] = [];

        const records = data['banks']
            .map((d: any, index: number) => {
                if (d['checked']) {
                    toRemoveItems.push(index);

                    let isFirstRow = true; // Track if it's the first row for setting id

                    // // Filter and process splitData to handle multiple records
                    // const splitDataForRecord = splitData
                    //     .filter((split: any) => {
                    //         if (split.rowID === d.record_id) {
                    //             if (isFirstRow) {
                    //                 isFirstRow = false;
                    //                 return true; // Include the first entry
                    //             } else {
                    //                 split.id = null; // Set id to null for subsequent entries
                    //                 return true;
                    //             }
                    //         }
                    //         return false;
                    //     })
                    //     .map((split: any) => {
                    //         const { rowID, ...rest } = split; // Remove rowID
                    //         return {
                    //             ...rest,
                    //             id: rest.id, // Only first item retains id
                    //             deposit_debit_account_id: rest.deposit_debit_account_id ? rest.deposit_debit_account_id.value : null,
                    //             deposit_credit_account_id: rest.deposit_credit_account_id ? rest.deposit_credit_account_id : null,
                    //         };
                    //     });

                    // Provide default split_data if no entries found
                    // const split = splitDataForRecord.length > 0 ? splitDataForRecord : [
                    //     {
                    //         id: d.record_id, // Only first entry should have id, others should be null
                    //         deposit: d.deposit,
                    //         deposit_debit_account_id: d['deposit_debit_account_id'] && d['deposit_debit_account_id']['value'] ? +d['deposit_debit_account_id']['value'] : null,
                    //         deposit_credit_account_id: d['deposit_credit_account_id'] && d['deposit_credit_account_id']['value'] ? +d['deposit_credit_account_id']['value'] : null,
                    //     }
                    // ].map((item, index) => ({
                    //     ...item,
                    //     id: index === 0 ? item.id : null // Ensure only the first item has an id
                    // }));
                    // Process the existing splitData within the record
                    //let isFirstRow = true; // Track if it's the first row for setting id

                    // Process the existing splitData within the record
                    let split = d['split']
                        .map((split: any) => {
                            if (isFirstRow) {
                                isFirstRow = false;
                                return {
                                    ...split,
                                    id: split.id, // Only first item retains id
                                    deposit_debit_account_id: split.deposit_debit_account_id ? split.deposit_debit_account_id.value : null,
                                    deposit_credit_account_id: split.deposit_credit_account_id ? split.deposit_credit_account_id : null,
                                };
                            } else {
                                return {
                                    ...split,
                                    id: null, // Set id to null for subsequent entries
                                    deposit_debit_account_id: split.deposit_debit_account_id ? split.deposit_debit_account_id.value : null,
                                    deposit_credit_account_id: split.deposit_credit_account_id ? split.deposit_credit_account_id : null,
                                };
                            }
                        })
                        .filter((item: any) => (item.deposit_debit_account_id !== null) || (item.deposit_credit_account_id !== null)); // Filter out splits with null debit and credit account ids

                    // If no valid split data found, add a default split entry
                    if (split.length === 0) {
                        split = [
                            {
                                id: d.record_id, // Only first entry should have id, others should be null
                                deposit: d.deposit,
                                deposit_debit_account_id: d['deposit_debit_account_id'] && d['deposit_debit_account_id']['value'] ? +d['deposit_debit_account_id']['value'] : null,
                                deposit_credit_account_id: d['deposit_credit_account_id'] && d['deposit_credit_account_id']['value'] ? +d['deposit_credit_account_id']['value'] : null,
                            },
                        ];
                    }

                    // Only include records that have valid split entries
                    if (split.length > 0) {
                        return {
                            id: d.record_id,
                            parent_id: d.parent_id,
                            description: d.description,
                            account_name: d.account_name,
                            transaction_at: d.transaction_at,
                            // Only a vendor chosen here; an unchanged saved one is not resent.
                            vendor_id: d.vendor_id && d.vendor_id !== d.saved_vendor_id ? d.vendor_id : null,
                            split: split // Add split_data array here
                        };
                    } else {
                        return null;
                    }
                } else {
                    return null;
                }
            })
            .filter((e: any) => e); // Filter out null values

        // A saved vendor swapped for another also moves its 1099 payment, so
        // the user is asked first, naming each expense and both vendors.
        const moves = data['banks']
            .map((d: any, index: number) => ({ d, index }))
            .filter(({ d }: any) => d['checked'] && d['saved_vendor_id'] && d['vendor_id'] && d['vendor_id'] !== d['saved_vendor_id'])
            .map(({ d, index }: any) => ({
                index,
                savedVendorId: d['saved_vendor_id'],
                description: d['description'],
                from: vendorNameById.get(Number(d['saved_vendor_id'])) || '',
                to: vendorNameById.get(Number(d['vendor_id'])) || '',
            }));
        if (moves.length && records.length) {
            setPendingPost({ records, toRemoveItems, moves });
            return;
        }
        postData(records, toRemoveItems);
    };
    const postData = (records: any, toRemoveItems: number[]) => {
        if (records.length) {
            setIsLoading(true);
            bankGlUpdate({ records, branch_id: +branchData['id'] }, (message: string, resp: any) => {
                setIsLoading(false);
                toast.success(message)
                // Tell the user what the vendor change did to 1099 reporting.
                const reassigned: any[] = resp?.data?.data?.vendor_reassignments || [];
                reassigned.forEach((move: any) => {
                    const drafts = (move.draft_forms || [])
                        .map((form: any) => `${form.vendor_name} ${form.tax_year} ${form.form_type}`)
                        .join(', ');
                    toast(t(move.payment_moved ? 'reassign.done_payment' : 'reassign.done_no_payment', {
                        description: move.description,
                        from: move.from_vendor?.name,
                        to: move.to_vendor?.name,
                        amount: usdCurrencyFormat(move.amount || 0),
                        year: move.tax_year,
                    }) + (drafts ? ' ' + t('reassign.regenerate', { forms: drafts }) : ''), { duration: 12000 });
                });
                formRemove(toRemoveItems)
                if (records.length == fields.length) {
                    navigate(-1);
                }
                // fetchTransactionList();
            }, (message: string, resp: any) => {
                setIsLoading(false);
                toast.error(message);
            })
        } else {
            toast.error(t('alert.select_one_record'));
        }
    }
    const processSearchData = (data: any) => {
        setSelectedCalendar({
            year: +data['year']['value'],
            month: +data['month']['value']
        })
    }
    const handleSinglePost = (index: number) => {
        const data = getValues();
        const toRemoveItems: number[] = [];

        if (data['banks'][index]['checked']) {
            // Filter splitData based on the record_id
            const splitDataForRecord = splitData.filter((split: any) => split.id === data['banks'][index]['record_id']);

            const records = [data['banks'][index]]
                .map((d: any) => {
                    return {
                        id: d.record_id,
                        parent_id: d.parent_id,
                        description: d.description,
                        account_name: d.account_name,
                        transaction_at: d.transaction_at,
                        deposit: d.deposit,
                        deposit_debit_account_id: d['deposit_debit_account_id'] && d['deposit_debit_account_id']['value'] ? +d['deposit_debit_account_id']['value'] : null,
                        deposit_credit_account_id: d['deposit_credit_account_id'] && d['deposit_credit_account_id']['value'] ? +d['deposit_credit_account_id']['value'] : null,
                        split: splitDataForRecord // Add split_data array here
                    }
                });
            //console.log(records);

            toRemoveItems.push(index);

            postData(records, toRemoveItems);
        } else {
            toast.error(t('alert.select_record'));
        }
    }

    const handleFieldValidation = async (index: number) => {
        // Trigger validation for the specified field within the array
        const isValidated = await trigger(`banks.${index}`);
        if (isValidated) {
            handleSinglePost(index)
        }
    };


    const selectRefs = useRef<SelectInstance<any, false, any>[]>([]);

const handleSelectChange = (displayIndex: number, e: any) => {
    setTimeout(() => {
        let nextDisplayIndex = displayIndex + 1;
        while (nextDisplayIndex < sortedIndices.length) {
            const targetFieldIndex = sortedIndices[nextDisplayIndex];
            const nextSelect = selectRefs.current[targetFieldIndex];
            if (nextSelect && !nextSelect.props.isDisabled) {
                nextSelect.focus();
                break;
            }
            nextDisplayIndex++;
        }
    }, 0);
};


const handleAutoSelectExpenseDropdown = (displayIndex: number) => {
    const currentFieldIndex = sortedIndices[displayIndex];
    const selectedValue = getValues(`banks.${currentFieldIndex}.deposit_debit_account_id`);
    for (let i = displayIndex + 1; i < sortedIndices.length; i++) {
        const nextFieldIndex = sortedIndices[i];
        if (fields[nextFieldIndex]?.deposit) {
            setValue(`banks.${nextFieldIndex}.deposit_debit_account_id`, selectedValue);
            selectRefs.current[nextFieldIndex]?.setValue(selectedValue, 'select-option');
        }
    }
};


    const sortedIndices = useMemo(() => {
    const indices = fields.map((_, i) => i);

    if (!sortConfig.key) return indices;

    return indices.sort((a, b) => {
        let valA: any;
        let valB: any;

        if (sortConfig.key === 'transaction_at') {
            valA = moment(fields[a]?.transaction_at).valueOf();
            valB = moment(fields[b]?.transaction_at).valueOf();
        } else {
            valA = (fields[a]?.description || '').toLowerCase();
            valB = (fields[b]?.description || '').toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });
}, [fields, sortConfig]);

const handleSort = (key: 'transaction_at' | 'description') => {
    setSortConfig(prev => {
        if (prev.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
        }
        return { key, direction: 'asc' };
    });
};

const getSortIcon = (key: 'transaction_at' | 'description') => {
    const isActive = sortConfig.key === key;

    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            className="ms-1"
            style={{ verticalAlign: 'middle' }}
        >
            <path
                d="M8 3L4 8h3v9h2V8h3L8 3z"
                fill={isActive && (sortConfig.direction === 'asc') ? '#0d6efd' : '#FFFFFF'}
            />
            <path
                d="M16 21l4-5h-3V7h-2v9h-3l4 5z"
                fill={isActive && (sortConfig.direction === 'desc') ? '#0d6efd' : '#FFFFFF'}
            />
        </svg>
    );
};

    return (
        <div className="main-container flex-grow-1">
            <div className="container-fluid">
                <div className="page-title pb-3 pt-3" id="ux_page_title">
                    <div className="row align-items-center">
                        <div className="col-sm-6 align-items-center d-flex">
                            <h1 className="h5 font-weight-700 mb-0 d-inline-flex">{t('title')}</h1>
                        </div>
                        <div className="col-sm-6 justify-content-end align-items-center d-flex">
                            <Link to={URLS.EXPENSE_GL_CLASSIFICATION_STATUS} className="btn btn-link">{t('button.back_to_status')}</Link>
                        </div>
                    </div>
                </div>

                <div className=" executive-dashboard">
                    <div className="dashboard-top-part">
                        <div className="filter-btn">
                            <div className={`page-loader ${isLoading ? 'visible' : ''}`}>
                                <div className="loader"></div>
                            </div>
                        </div>
                        <Form>
                            <div className="modal-footer">
                            {
                                ((fields.length > 0) && (
                                    <>
                                    <button type="button" className="btn btn-primary" onClick={handleSubmit(processData)}>{t('button.post')}</button>&nbsp;&nbsp;
                                    </>
                                )) || <></>
                            }
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCOAModal(true)}>{t('button.add_coa')}</button>
                            </div>

                            <br />
                            <div className="table-responsive list-table table-scroll" style={{ maxHeight: contentMaxHeight }}>
                                <table className="table table-default">
                                    <thead>
                                        <tr className="align-middle">
                                            <th className="w-5 text-center">
                                                <div className="form-check">
                                                    <label>
                                                        <input ref={checkAllRef} className="form-check-input wh-20 checkAll" type="checkbox" onChange={handleCheckAllChange} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSort('transaction_at')}>
                                                {t('table.date')}{getSortIcon('transaction_at')}
                                            </th>
                                            <th className="text-start" style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>
                                                {t('table.description')}{getSortIcon('description')}
                                            </th>
                                            <th className="text-start">{t('table.vendor')}</th>
                                            <th className="text-end">{t('table.expense')}</th>
                                            <th className="text-start">{t('table.debit_gl_account')}</th>
                                            <th className="text-start">{t('table.credit_gl_account')}</th>
                                            <th className="text-center">{t('table.action')}</th> {/* Added column for actions */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            fields.length > 0 ?
                                            sortedIndices.map((index: number,displayIndex: number) => {
                                                const data = fields[index];
                                                const hasSplitData = splitData.some((split: any) => split.id === data.record_id);
                                                const splitDebitAccounts = splitData
                                                    .filter((split) => split.id === data.record_id)
                                                    .map((split) => split.deposit_debit_account_id.label)
                                                    .join(', ');

                                                    return (
                                                        <tr className="align-middle h-60" key={data['id']}>
                                                            <td className="text-center">
                                                                <div className="form-check">
                                                                    <label>
                                                                        <input className="form-check-input wh-20 checkbox" {...register(`banks.${index}.checked`)}
                                                                            name={`banks.${index}.checked`}
                                                                            defaultChecked={fields[index]['checked']}
                                                                            type="checkbox" onChange={(e: any) => handleCheckAll(e, index)} />
                                                                    </label>
                                                                </div>
                                                            </td>
                                                            <td className="text-center"><strong>{formatTransactionDate(fields[index].transaction_at)}</strong></td>
                                                            <td className="text-start text-truncate" style={{ maxWidth: '250px' }} title={fields[index].description || t('message.na')}>{fields[index].description || t('message.na')}</td>
                                                            {/* Every row gets the dropdown. It starts on the vendor already
                                                                saved on the expense (Payee Review, an earlier post) or, for a
                                                                check, the one vendor its payee names. A saved vendor cannot be
                                                                cleared, but can be swapped: the 1099 payment moves with it, and
                                                                Post asks for confirmation first. */}
                                                            <td className="text-start" style={{ minWidth: '200px' }}>
                                                                <Controller
                                                                    control={control}
                                                                    name={`banks.${index}.vendor_id`}
                                                                    render={({ field: { value } }) => {
                                                                        // A saved vendor that has since been deactivated is not an
                                                                        // option, but must still show as the current value.
                                                                        const selected = vendorOptions.find((option) => option.value === Number(value))
                                                                            || (value && vendorNameById.get(Number(value))
                                                                                ? { value: Number(value), label: vendorNameById.get(Number(value)) as string }
                                                                                : null);
                                                                        return (
                                                                            <Select
                                                                                isClearable={!fields[index].saved_vendor_id}
                                                                                isSearchable
                                                                                placeholder={t('dropdown.placeholder.vendor')}
                                                                                options={vendorOptions}
                                                                                value={selected}
                                                                                onChange={(option: any) => {
                                                                                    const vendorId = option ? option.value : null;
                                                                                    setValue(`banks.${index}.vendor_id`, vendorId, { shouldDirty: true });
                                                                                    // A vendor the user just chose must not be put back by the payee match.
                                                                                    preselectedRecordIds.current.add(fields[index].record_id);
                                                                                    // Only an expense that had no vendor takes the vendor's default
                                                                                    // account. Swapping a saved vendor changes the vendor alone: the
                                                                                    // expense was already classified.
                                                                                    if (!fields[index].saved_vendor_id) {
                                                                                        applyVendorDefaultAccount(index, vendorId, true);
                                                                                    }
                                                                                }}
                                                                                menuPortalTarget={document.body}
                                                                                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                                                                            />
                                                                        );
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="text-end">{usdCurrencyFormat(Math.abs(fields[index].deposit))}</td>
                                                            {
                                                                hasSplitData ? (
                                                                    <>
                                                                        <td className="text-start text-truncate" style={{ maxWidth: '250px' }} title={splitDebitAccounts || t('message.na')}>
                                                                            {splitDebitAccounts}
                                                                        </td>
                                                                        <td className="text-start">
                                                                            <Form.Group className="col-sm-12 d-flex" controlId="formBasicEmail">
                                                                                <Controller
                                                                                    control={control}
                                                                                    name={`banks.${index}.deposit_credit_account_id`}
                                                                                    render={({ field: { value, onChange, onBlur } }) => (
                                                                                        <AsyncSelect

                                                                                            isClearable={true}
                                                                                            placeholder={t('dropdown.placeholder.gl_account')}
                                                                                            defaultOptions={glCreditAccount}
                                                                                            loadOptions={filterCreditAccountList}
                                                                                            // onChange={(e:any) => {
                                                                                            //     onChange(e);
                                                                                            //     // Manually blur the input field to remove focus
                                                                                            //     const activeElement = document.activeElement as HTMLElement;
                                                                                            //     activeElement?.blur();
                                                                                            //   }}
                                                                                            onChange={(e: any) => {
                                                                                                onChange(e);
                                                                                               handleSelectChange(displayIndex, e);
                                                                                            }}
                                                                                            className='vp-dropdown'
                                                                                            menuPosition='fixed'
                                                                                            value={value}
                                                                                            defaultValue={value}
                                                                                            styles={selectCustomStyle}
                                                                                            components={{ Option: SelectCustomOption }}
                                                                                            isDisabled={!!fields[index].deposit_credit_account_id}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                                {
                                                                                    !!fields[index].deposit_credit_account_id && <input {...register(`banks.${index}.deposit_credit_account_id`)} type="hidden" />
                                                                                }
                                                                            </Form.Group>
                                                                            {
                                                                                (errors && errors.banks && errors.banks[index] && errors.banks[index]?.deposit_credit_account_id && errors.banks[index]?.deposit_credit_account_id?.message && (
                                                                                    <Form.Text className="text-danger d-flex">
                                                                                        <strong>
                                                                                            {errors.banks[index]?.deposit_credit_account_id?.message as ReactNode || ''}
                                                                                        </strong>
                                                                                    </Form.Text>
                                                                                )) || ''
                                                                            }
                                                                        </td>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <td className="text-start">
                                                                            <Form.Group className="col-sm-12 d-flex align-items-center" controlId="formBasicEmail">
                                                                                <Controller
                                                                                    control={control}
                                                                                    disabled={!fields[index].deposit}
                                                                                    name={`banks.${index}.deposit_debit_account_id`}
                                                                                    render={({ field: { value, onChange, disabled } }) => (
                                                                                        <AsyncSelect
                                                                                            ref={(el) => (selectRefs.current[index] = el as SelectInstance<any, false, any>)}
                                                                                            isClearable={true}
                                                                                            placeholder={t('dropdown.placeholder.gl_account')}
                                                                                            defaultOptions={glDebitAccount}
                                                                                            loadOptions={filterDebitAccountList}
                                                                                            onChange={(e: any) => {
                                                                                                onChange(e);
                                                                                                handleSelectChange(displayIndex, e);
                                                                                            }}
                                                                                            className='vp-dropdown'
                                                                                            menuPosition='fixed'
                                                                                            value={value}
                                                                                            defaultValue={value}
                                                                                            isDisabled={disabled}
                                                                                            styles={selectCustomStyle}
                                                                                            components={{ Option: SelectCustomOption }}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link p-0 ms-2"
                                                                                    onClick={() => handleAutoSelectExpenseDropdown(displayIndex)}
                                                                                >
                                                                                    <i className="cursor-pointer fa fa-arrow-alt-circle-down" title={t('tooltip.apply_below')}></i>
                                                                                </button>
                                                                            </Form.Group>
                                                                            {
                                                                                (errors && errors.banks && errors.banks[index] && errors.banks[index]?.deposit_debit_account_id && errors.banks[index]?.deposit_debit_account_id?.message && (
                                                                                    <Form.Text className="text-danger d-flex">
                                                                                        <strong>
                                                                                            {errors.banks[index]?.deposit_debit_account_id?.message as ReactNode || ''}
                                                                                        </strong>
                                                                                    </Form.Text>
                                                                                )) || ''
                                                                            }
                                                                        </td>
                                                                        <td className="text-start">
                                                                            <Form.Group className="col-sm-12 d-flex" controlId="formBasicEmail">
                                                                                <Controller
                                                                                    control={control}
                                                                                    name={`banks.${index}.deposit_credit_account_id`}
                                                                                    render={({ field: { value, onChange, onBlur } }) => (
                                                                                        <AsyncSelect
                                                                                            isClearable={true}
                                                                                            placeholder={t('dropdown.placeholder.gl_account')}
                                                                                            defaultOptions={glCreditAccount}
                                                                                            loadOptions={filterCreditAccountList}
                                                                                            onChange={(e: any) => {
                                                                                                onChange(e);
                                                                                                // Manually blur the input field to remove focus
                                                                                                const activeElement = document.activeElement as HTMLElement;
                                                                                                activeElement?.blur();
                                                                                            }}
                                                                                            className='vp-dropdown'
                                                                                            menuPosition='fixed'
                                                                                            value={value}
                                                                                            defaultValue={value}
                                                                                            styles={selectCustomStyle}
                                                                                            components={{ Option: SelectCustomOption }}
                                                                                            isDisabled={!!fields[index].deposit_credit_account_id}
                                                                                        />
                                                                                    )}
                                                                                />
                                                                                {
                                                                                    !!fields[index].deposit_credit_account_id && <input {...register(`banks.${index}.deposit_credit_account_id`)} type="hidden" />
                                                                                }
                                                                            </Form.Group>
                                                                            {
                                                                                (errors && errors.banks && errors.banks[index] && errors.banks[index]?.deposit_credit_account_id && errors.banks[index]?.deposit_credit_account_id?.message && (
                                                                                    <Form.Text className="text-danger d-flex">
                                                                                        <strong>
                                                                                            {errors.banks[index]?.deposit_credit_account_id?.message as ReactNode || ''}
                                                                                        </strong>
                                                                                    </Form.Text>
                                                                                )) || ''
                                                                            }
                                                                        </td>
                                                                    </>
                                                                )
                                                            }
                                                            <td className="text-center">
                                                                <Button variant="primary" onClick={() => handleSplitClick(data, fields[index].record_id)}>{hasSplitData ? t('button.modify_split') : t('button.split')}</Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                }) :
                                                <tr>
                                                    <td colSpan={8} className='text-center'>{t('empty.no_records')}</td>
                                                </tr>
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>
            <SAlert
                show={pendingPost !== null}
                icon="warning"
                showCancel={true}
                confirmBtnText={t('reassign.confirm')}
                cancelBtnText={t('reassign.cancel')}
                confirmBtnBsStyle={'primary'}
                cancelBtnBsStyle={'secondary'}
                title={t('reassign.title')}
                // Plain text: SAlert's message is not rendered as JSX.
                msg={pendingPost ? [
                    t('reassign.intro'),
                    '',
                    ...pendingPost.moves.map((move: any) => `• ${move.description}: ${move.from} → ${move.to}`),
                    '',
                    t('reassign.prompt'),
                ].join('\n') : ''}
                onConfirm={() => {
                    const pending = pendingPost;
                    setPendingPost(null);
                    if (pending) postData(pending.records, pending.toRemoveItems);
                }}
                onCancel={() => {
                    // Declined: put each row back on its saved vendor.
                    (pendingPost?.moves || []).forEach((move: any) => {
                        setValue(`banks.${move.index}.vendor_id`, move.savedVendorId, { shouldDirty: true });
                    });
                    setPendingPost(null);
                }}
            />
            <SplitModal
                rowID={selectedRecord?.record_id || 0}
                indexNo={selectedRecord?.formIndex || 0}
                show={showSplitModal}
                onHide={() => setShowSplitModal(false)}
                onSave={handleSplitSave}
                originalAmount={selectedRecord?.deposit || 0}
                glDebitAccounts={glDebitAccount}
                creditAccountName={selectedRecord?.deposit_credit_account_id?.label || ''}
                creditAccountId={selectedRecord?.deposit_credit_account_id?.value || ''}
                transactionAt={selectedRecord?.transaction_at || ''}
                description={selectedRecord?.description || ''}
                splitData={splitData}
                filterDebitAccountList={filterDebitAccountList}
            />

            {
                (showCOAModal && <ChartAccountModal closeModal={() => setShowCOAModal(false)} addRecord={true} record={null} />) || <></>
            }
        </div>
    )
}

export default ExpenseGlClassification;